// vpn-web-bo/src/schemas/promoCode.ts
import { z } from 'zod';

/** validitySeconds 上限（= 24×365×10×3600 ≈ 10 年），對齊後端 validateBounds。 */
export const MAX_VALIDITY_SECONDS = 24 * 365 * 10 * 3600;
/** perDeviceRedeemLimit 上限，對齊後端 validateBounds（num(...,0,1_000_000)）。 */
export const MAX_PER_DEVICE_REDEEM_LIMIT = 1_000_000;

/**
 * VPN-81 有效時長：權威單位是「秒」（validitySeconds），但 Jira 第九章要求後台以
 * 「分鐘」輸入（支援 0.1 分 = 6 秒的短時效測試碼）。以下兩個純函數負責 UI 分鐘 ↔
 * 權威秒的換算，抽出來讓表單與單元測試共用（避免各處手刻換算飄移）。
 */
export function minutesToSeconds(min: number): number {
  return Math.round(min * 60);
}
export function secondsToMinutes(sec: number): number {
  return sec / 60;
}

/** 節點等級上限（對齊後端 ip.service.ts:2587 `minTier < 0 || minTier > 999` 範圍 0-999）。 */
export const MAX_NODE_TIER = 999;

/**
 * VPN-81 B5 — 節點等級 ACL（軸3）。後端 entity 型別 number[] | null：
 *   null/省略 = 不限（allow-all）、[] = deny-all（空白名單 = 全部拒絕）、
 *   [t…] = 白名單（node.minTier ∈ 清單才放行）。
 * 後台明確區分三態：不限（null）/ 全部拒絕（[]）/ 指定白名單（[t…]），
 * 見 promoFields.AllowedNodeTiersField。schema 對每個等級補 max(999) 上限，
 * 對齊後端 node.minTier 範圍（避免 [1000] 這類「過 schema 但無節點可匹配」的死值）。
 */
const AllowedNodeTiersSchema = z
  .array(
    z
      .number()
      .int('節點等級必須為整數')
      .min(0, '節點等級不可為負')
      .max(MAX_NODE_TIER, `節點等級不可超過 ${MAX_NODE_TIER}`)
  )
  .nullable()
  .optional();

/** VPN-81 B2 — 單裝置兌換次數上限（int，0 = 不限）。 */
const PerDeviceRedeemLimitSchema = z
  .number()
  .int('單裝置兌換次數上限必須為整數')
  .min(0)
  .max(MAX_PER_DEVICE_REDEEM_LIMIT)
  .default(0);

export const PromoCodeUpsert = z.object({
  // 去品牌化 M4：寫入必須顯式指定目標品牌，不沿用「檢視範圍」（對齊 user.ts）。
  targetBrand: z
    .string({ message: '請選擇目標品牌' })
    .regex(/^[a-z][a-z0-9_]{1,15}$/, '請選擇目標品牌'),
  code: z.string().min(1, '兌換碼必填').max(64),
  name: z.string().max(128).optional().default(''),
  advertiser: z.string().max(128).nullable().optional(),
  campaign: z.string().max(128).nullable().optional(),
  grantMb: z.number().int().min(0).default(1024),
  // VPN-81：有效時長改以「秒」為權威單位（int），支援秒級短時效測試碼。
  // 表單以分鐘輸入，送出前換算成本欄位（見 promoFields.ValidityMinutesField）。
  validitySeconds: z
    .number()
    .int('有效秒數必須為整數')
    .min(1, '有效秒數最少 1 秒')
    .max(MAX_VALIDITY_SECONDS)
    .default(86400),
  perDeviceCapMb: z.number().int().min(0).default(3072),
  // VPN-81 B2 — 單裝置兌換次數上限（與 perDeviceCapMb 並存 AND）。
  perDeviceRedeemLimit: PerDeviceRedeemLimitSchema,
  // VPN-81 B5 — 節點等級白名單（軸3 ACL）。
  allowedNodeTiers: AllowedNodeTiersSchema,
  totalRedemptionLimit: z.number().int().min(0).default(0),
  startAt: z.string().nullable().optional(),
  endAt: z.string().nullable().optional(),
  status: z.enum(['active', 'disabled']).default('active'),
});
export type PromoCodeUpsertValues = z.infer<typeof PromoCodeUpsert>;

export const BatchGenerate = z.object({
  // 去品牌化 M4：批量產碼同樣顯式指定目標品牌，不沿用「檢視範圍」。
  targetBrand: z
    .string({ message: '請選擇目標品牌' })
    .regex(/^[a-z][a-z0-9_]{1,15}$/, '請選擇目標品牌'),
  count: z.number().int().min(1).max(1000).default(50),
  prefix: z.string().max(32).optional().default('WD-'),
  name: z.string().max(128).optional().default(''),
  campaign: z.string().max(128).nullable().optional(),
  grantMb: z.number().int().min(0).default(1024),
  // VPN-81：有效時長改以「秒」為權威單位（int），支援秒級短時效測試碼。
  validitySeconds: z
    .number()
    .int('有效秒數必須為整數')
    .min(1, '有效秒數最少 1 秒')
    .max(MAX_VALIDITY_SECONDS)
    .default(86400),
  perDeviceCapMb: z.number().int().min(0).default(3072),
  // VPN-81 B2/B5 — 批量產碼共用同一組次數上限 / 節點等級 ACL。
  perDeviceRedeemLimit: PerDeviceRedeemLimitSchema,
  allowedNodeTiers: AllowedNodeTiersSchema,
  totalRedemptionLimit: z.number().int().min(0).default(0),
});
export type BatchGenerateValues = z.infer<typeof BatchGenerate>;
